// Draws the shared well onto a canvas.
//
// Colour belongs to PEOPLE, not shapes: your falling piece is your colour,
// and so is every block you lock. On a crowded well that's the only way to
// find your own piece at a glance, and the finished stack ends up as a map
// of who built what. (It also keeps us well clear of the classic
// colour-per-shape look - this game needs a look of its own.)

import { HIDDEN_ROWS, VISIBLE_ROWS, pieceCells, ROTATIONS } from '../engine/game.js';
import { colour } from '../account/palette.js';

// Each seat's colour comes from its player's pick (see account/palette.js)
// and is passed in as `colours`; this is only the fallback order.
export const PLAYER_COLOURS = ['coral', 'teal', 'gold', 'violet'].map(colour);

const BG = '#12141c';
const WELL = '#1a1d29';
const GRIDLINE = 'rgba(255,255,255,0.035)';
const NEXT_ROWS = 3; // strip above the well for each player's next piece

// Effects the renderer keeps for itself (flashes, shoves, score pops).
export function createFx() {
  return {
    flashes: [], shoves: [], pops: [], spawns: [],
  };
}

// How long the "here you are" flash lasts on a new piece.
const SPAWN_FX_MS = 900;

export function noteEvents(fx, events, now) {
  for (const e of events) {
    if (e.type === 'clear') {
      fx.flashes.push({ rows: e.rows, at: now });
      fx.pops.push({
        text: e.owners.length > 1 ? `+${e.points}  ×${e.owners.length} together` : `+${e.points}`,
        row: e.rows[0],
        at: now,
      });
    }
    if (e.type === 'spawn') {
      fx.spawns = fx.spawns.filter((sp) => sp.id !== e.playerId);
      fx.spawns.push({ id: e.playerId, at: now });
    }
    if (e.type === 'push') {
      e.pushed.forEach((id) => fx.shoves.push({ id, dx: e.dx, at: now }));
    }
  }
}

// Room kept clear down the left for the score (and matched on the right so
// the well stays centred) - with twelve players the well is 62 wide and
// would otherwise run underneath it.
const SIDE_GUTTER = 120;

// A tall screen (a desktop, a TV, an iPad) gives the well as much height as
// it can take (Matt, 2026-10-09: "make the well as big as we can on a large
// screen without risking any layout issues or scrolling"). The strip above
// it only ever holds a name and a piece drawn at 0.55 of a cell - two rows
// tall at most - so 2.2 rows hold it, and the 1.5 rows of slack shrink to
// 0.6. That's ~8% more well. Phones pass their own nextRows and keep the
// roomier sums.
const TALL = 700;
const TALL_NEXT_ROWS = 2.2;

export function layout(width, cssW, cssH, nextRows = NEXT_ROWS) {
  const tall = cssH >= TALL && nextRows === NEXT_ROWS;
  const next = tall ? TALL_NEXT_ROWS : nextRows;
  const rows = VISIBLE_ROWS + next + (tall ? 0.6 : 1.5);
  const sides = cssW > 900 ? SIDE_GUTTER * 2 : 32;
  const cell = Math.floor(Math.min((cssH - 24) / rows, (cssW - sides) / width));
  const wellW = cell * width;
  const wellH = cell * VISIBLE_ROWS;
  const x0 = Math.round((cssW - wellW) / 2);
  const y0 = Math.round((cssH - wellH - cell * (next + 0.5)) / 2 + cell * (next + 0.5));
  return { cell, x0, y0, wellW, wellH, nextRows: next };
}

// A repeated colour's mark (2026-10-30: forty seats, twenty colours): the
// second player with a colour gets a dot, the third a ring, the fourth a slash.
function mark(ctx, x, y, s, kind) {
  if (!kind) return;
  const c = s / 2;
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = Math.max(1.5, s * 0.12);
  ctx.beginPath();
  if (kind === 1) { ctx.arc(x + c, y + c, s * 0.16, 0, Math.PI * 2); ctx.fill() }
  else if (kind === 2) { ctx.arc(x + c, y + c, s * 0.22, 0, Math.PI * 2); ctx.stroke() }
  else { ctx.moveTo(x + s * 0.28, y + s * 0.72); ctx.lineTo(x + s * 0.72, y + s * 0.28); ctx.stroke() }
  ctx.restore();
}

/** Each seat's mark: 0 for the first to wear a colour, 1, 2, 3 for those after. */
export function repeatMarks(colourList) {
  const seen = {};
  return colourList.map((c) => {
    const key = c?.fill || '';
    seen[key] = (seen[key] ?? -1) + 1;
    return Math.min(3, seen[key]);
  });
}

function block(ctx, x, y, s, fill, alpha = 1) {
  const r = Math.max(2, s * 0.18);
  const inset = Math.max(1, s * 0.06);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.roundRect(x + inset, y + inset, s - inset * 2, s - inset * 2, r);
  ctx.fill();
  // A soft top highlight so blocks read as tiles, not flat squares.
  ctx.fillStyle = 'rgba(255,255,255,0.18)';
  ctx.beginPath();
  ctx.roundRect(x + inset * 2, y + inset * 2, s - inset * 4, (s - inset * 4) * 0.35, r * 0.6);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function outline(ctx, x, y, s, stroke, alpha) {
  const inset = Math.max(1.5, s * 0.1);
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = Math.max(1.5, s * 0.07);
  ctx.beginPath();
  ctx.roundRect(x + inset, y + inset, s - inset * 2, s - inset * 2, Math.max(2, s * 0.16));
  ctx.stroke();
  ctx.globalAlpha = 1;
}

export function draw(ctx, state, fx, {
  cssW, cssH, now, labels = [], colours = PLAYER_COLOURS,
  // Party mode: a phone shows only its own next piece (nextOnly = its
  // seat); the TV shows nobody's (too many to fit) but tags every falling
  // piece with its player's name, since nobody has a seat card to look at.
  nextOnly = null, showNext = true, tags = false,
  // Rows of space above the well for the next pieces (a phone, showing
  // only its own, needs less and gets a bigger board for it).
  nextRows = NEXT_ROWS,
}) {
  const pal = (i) => colours[i] || PLAYER_COLOURS[i % PLAYER_COLOURS.length];
  const marks = repeatMarks(state.players.map((_, i) => pal(i)));
  const L = layout(state.width, cssW, cssH, nextRows);
  const { cell, x0, y0 } = L;
  const toY = (row) => y0 + (row - HIDDEN_ROWS) * cell;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, cssW, cssH);

  // Well
  ctx.fillStyle = WELL;
  ctx.beginPath();
  ctx.roundRect(x0 - 6, y0 - 6, L.wellW + 12, L.wellH + 12, 10);
  ctx.fill();
  ctx.strokeStyle = GRIDLINE;
  ctx.lineWidth = 1;
  for (let c = 1; c < state.width; c += 1) {
    ctx.beginPath(); ctx.moveTo(x0 + c * cell + 0.5, y0); ctx.lineTo(x0 + c * cell + 0.5, y0 + L.wellH); ctx.stroke();
  }
  for (let r = 1; r < VISIBLE_ROWS; r += 1) {
    ctx.beginPath(); ctx.moveTo(x0, y0 + r * cell + 0.5); ctx.lineTo(x0 + L.wellW, y0 + r * cell + 0.5); ctx.stroke();
  }

  // Stack
  for (let row = HIDDEN_ROWS; row < state.height; row += 1) {
    for (let col = 0; col < state.width; col += 1) {
      const c = state.grid[row][col];
      if (c) { block(ctx, x0 + col * cell, toY(row), cell, pal(c.owner).dim); mark(ctx, x0 + col * cell, toY(row), cell, marks[c.owner]); }
    }
  }

  // Line-clear flashes
  fx.flashes = fx.flashes.filter((f) => now - f.at < 260);
  fx.flashes.forEach((f) => {
    const a = 1 - (now - f.at) / 260;
    ctx.fillStyle = `rgba(255,255,255,${0.55 * a})`;
    f.rows.forEach((row) => ctx.fillRect(x0, toY(row), L.wellW, cell));
  });

  // Falling pieces. No landing shadow - Matt, 2026-10-01: "I don't like the
  // shadow where it shows where my piece is going to drop ... Just get rid of it."
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, y0, L.wellW, L.wellH);
  ctx.clip();
  fx.shoves = fx.shoves.filter((s) => now - s.at < 140);
  state.players.forEach((p, i) => {
    if (!p.piece) return;
    // A shoved piece arrives with a little overshoot, so a push reads as a
    // bump rather than a teleport.
    const shove = fx.shoves.find((s) => s.id === i);
    const kick = shove ? shove.dx * cell * 0.22 * (1 - (now - shove.at) / 140) : 0;
    pieceCells(p.piece).forEach(([x, y]) => {
      block(ctx, x0 + x * cell + kick, toY(y), cell, pal(i).fill, y < HIDDEN_ROWS ? 0.5 : 1);
      mark(ctx, x0 + x * cell + kick, toY(y), cell, marks[i]);
      if (shove) outline(ctx, x0 + x * cell + kick, toY(y), cell, '#fff', 0.6 * (1 - (now - shove.at) / 140));
    });
  });
  ctx.restore();

  // "Here you are": pieces come in at a random spot, so each new one
  // arrives with a column of its colour shining down the well beneath it
  // (where it's headed), two rings pulsing out around it, and its name
  // above it.
  fx.spawns = fx.spawns.filter((sp) => now - sp.at < SPAWN_FX_MS);
  fx.spawns.forEach((sp) => {
    const p = state.players[sp.id];
    if (!p?.piece) return;
    const t = (now - sp.at) / SPAWN_FX_MS;
    const cells = pieceCells(p.piece);
    const left = Math.min(...cells.map((c) => c[0]));
    const right = Math.max(...cells.map((c) => c[0])) + 1;
    const top = Math.min(...cells.map((c) => c[1]));
    const bottom = Math.max(...cells.map((c) => c[1])) + 1;
    const { fill } = pal(sp.id);
    const bx = x0 + left * cell;
    const bw = (right - left) * cell;
    const fade = 1 - t;

    const beamTop = toY(bottom);
    const beamBottom = Math.min(beamTop + cell * 8, y0 + L.wellH);
    const beam = ctx.createLinearGradient(0, beamTop, 0, beamBottom);
    beam.addColorStop(0, fill);
    beam.addColorStop(1, `${fill}00`);
    ctx.globalAlpha = 0.28 * fade;
    ctx.fillStyle = beam;
    ctx.fillRect(bx, beamTop, bw, beamBottom - beamTop);

    // Two rings, the second a beat behind the first.
    [0, 0.25].forEach((delay) => {
      const k = Math.min(Math.max((t - delay) / 0.75, 0), 1);
      if (k <= 0 || k >= 1) return;
      const grow = cell * (0.15 + 1.3 * k);
      ctx.globalAlpha = 0.9 * (1 - k);
      ctx.strokeStyle = fill;
      ctx.lineWidth = Math.max(2, cell * 0.12);
      ctx.beginPath();
      ctx.roundRect(bx - grow, toY(top) - grow, bw + grow * 2, (bottom - top) * cell + grow * 2, cell * 0.3 + grow * 0.5);
      ctx.stroke();
    });
    ctx.globalAlpha = 1;
  });

  // Name tags ride above each new piece.
  fx.spawns.forEach((sp) => {
    const p = state.players[sp.id];
    if (!p?.piece || !labels[sp.id]) return;
    const t = (now - sp.at) / SPAWN_FX_MS;
    const cells = pieceCells(p.piece);
    const cx = x0 + ((Math.min(...cells.map((c) => c[0])) + Math.max(...cells.map((c) => c[0])) + 1) / 2) * cell;
    // In the light column just under the piece: above it is the row of
    // next pieces, and the dark outline keeps it readable as a ring passes.
    const ty = toY(Math.max(...cells.map((c) => c[1])) + 1) + cell * 0.8;
    ctx.globalAlpha = t < 0.7 ? 1 : (1 - t) / 0.3;
    ctx.font = `700 ${Math.max(11, Math.round(cell * 0.5))}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.lineWidth = 4;
    ctx.strokeStyle = BG;
    ctx.strokeText(labels[sp.id], cx, ty);
    ctx.fillStyle = pal(sp.id).fill;
    ctx.fillText(labels[sp.id], cx, ty);
    ctx.globalAlpha = 1;
  });

  if (tags) {
    ctx.font = `700 ${Math.max(10, Math.round(cell * 0.6))}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.lineWidth = 3;
    state.players.forEach((p, i) => {
      if (!p.piece || !labels[i] || fx.spawns.some((sp) => sp.id === i)) return;
      const cells = pieceCells(p.piece);
      const cx = x0 + ((Math.min(...cells.map((c) => c[0])) + Math.max(...cells.map((c) => c[0])) + 1) / 2) * cell;
      const ty = toY(Math.min(...cells.map((c) => c[1]))) - cell * 0.25;
      ctx.strokeStyle = BG;
      ctx.strokeText(labels[i], cx, ty);
      ctx.fillStyle = pal(i).fill;
      ctx.fillText(labels[i], cx, ty);
    });
  }

  // Next pieces, in a row of slots above the well (one per player - not a
  // lane any more; pieces come in anywhere).
  const nextY = y0 - cell * (L.nextRows + 0.2);
  const mini = cell * 0.55;
  const slot = nextOnly === null ? state.width / state.players.length : state.width;
  state.players.forEach((p, i) => {
    if (!showNext || (nextOnly !== null && i !== nextOnly)) return;
    const shape = p.queue[0];
    if (!shape) return;
    const cells = ROTATIONS[shape][0];
    const minX = Math.min(...cells.map((c) => c[0]));
    const maxX = Math.max(...cells.map((c) => c[0]));
    const minY = Math.min(...cells.map((c) => c[1]));
    const wPx = (maxX - minX + 1) * mini;
    const at = nextOnly === null ? i : 0;
    const cx = x0 + (slot * at + slot / 2) * cell - wPx / 2;
    cells.forEach(([x, y]) => block(ctx, cx + (x - minX) * mini, nextY + (y - minY) * mini + mini * 0.4, mini, pal(i).fill, 0.85));
    if (labels[i]) {
      ctx.fillStyle = pal(i).fill;
      ctx.font = `600 ${Math.max(10, Math.round(cell * 0.42))}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(labels[i], x0 + (slot * at + slot / 2) * cell, nextY - mini * 0.1);
    }
  });

  // Score pops
  fx.pops = fx.pops.filter((pp) => now - pp.at < 1100);
  fx.pops.forEach((pp) => {
    const t = (now - pp.at) / 1100;
    ctx.globalAlpha = 1 - t;
    ctx.fillStyle = '#fff';
    ctx.font = `700 ${Math.round(cell * 0.8)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(pp.text, x0 + L.wellW / 2, toY(pp.row) - t * cell * 2);
    ctx.globalAlpha = 1;
  });

  return L;
}
